import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Modal, Select, Space, Spin, Table, Tag, Typography } from 'antd';
import { FolderOpenOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import httpClient from '@api/httpClient';
import type { PaginatedResult } from '@/types';

interface BasRefDto {
  id: string;
  code?: string;
  name: string;
  isGroup?: boolean;
  parentId?: string;
}

interface BasRefPickerProps {
  /** BAS-ім'я типу як є (без префіксу `BasRef:`). Передається у /api/bas-refs/external/{basTypeName}. */
  acqEntityName: string;
  /** Title для UI. */
  acqTitle?: string;
  acqValue?: string | null;
  acqOnChange?: (acqId: string | null, acqLabel?: string) => void;
  acqReadOnly?: boolean;
  acqAllowClear?: boolean;
  acqDisabled?: boolean;
  acqSize?: 'small' | 'middle' | 'large';
}

const ACQ_ENDPOINT = '/bas-refs/external';

async function acqFetchList(acqEntity: string, acqSearch: string, acqLimit: number): Promise<BasRefDto[]> {
  const acqRes = await httpClient.get<PaginatedResult<BasRefDto>>(
    `${ACQ_ENDPOINT}/${encodeURIComponent(acqEntity)}`,
    { params: { search: acqSearch || undefined, page: 1, pageSize: acqLimit } },
  );
  return acqRes.data.items ?? [];
}

async function acqFetchOne(acqEntity: string, acqId: string): Promise<BasRefDto | null> {
  try {
    const acqRes = await httpClient.get<BasRefDto>(`${ACQ_ENDPOINT}/${encodeURIComponent(acqEntity)}/${acqId}`);
    return acqRes.data;
  } catch {
    return null;
  }
}

/**
 * Picker для catch-all BAS-довідників (`BasRef:*`) без типізованого Domain-entity.
 * Підключається через generic /api/bas-refs/external/{basTypeName}.
 *
 * Phase 7g (Sprint 20+ Refactor): well-known типи з Domain CRUD-UI (CashFlowItem,
 * Tax, TaxPurpose, TaxDeclarationItem, AccrualGroup) переїхали на ReferencePicker
 * через unified /api/references/{typeCode}. Цей компонент тепер обслуговує ЛИШЕ
 * external catch-all (записи з таблиці bas_external_refs).
 */
export default function BasRefPicker({
  acqEntityName, acqTitle, acqValue, acqOnChange, acqReadOnly = false,
  acqAllowClear = true, acqDisabled, acqSize = 'middle',
}: BasRefPickerProps) {
  const [acqQuery, setAcqQuery] = useState('');
  const [acqDebounced, setAcqDebounced] = useState('');
  const acqDebounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (acqDebounceRef.current) clearTimeout(acqDebounceRef.current);
    acqDebounceRef.current = setTimeout(() => setAcqDebounced(acqQuery), 250);
    return () => { if (acqDebounceRef.current) clearTimeout(acqDebounceRef.current); };
  }, [acqQuery]);

  const acqCurrentQ = useQuery({
    queryKey: ['basref-ext', acqEntityName, 'one', acqValue],
    queryFn: () => acqValue ? acqFetchOne(acqEntityName, acqValue) : Promise.resolve(null),
    enabled: !!acqValue,
    staleTime: 5 * 60_000,
  });

  const acqSearchQ = useQuery({
    queryKey: ['basref-ext', acqEntityName, 'search', acqDebounced],
    queryFn: () => acqFetchList(acqEntityName, acqDebounced, 30),
    enabled: !acqReadOnly,
    staleTime: 30_000,
  });

  const acqOptions = useMemo(() => {
    const acqList = acqSearchQ.data ?? [];
    const acqMap = new Map(acqList.map((i) => [i.id, i]));
    if (acqCurrentQ.data && !acqMap.has(acqCurrentQ.data.id)) acqMap.set(acqCurrentQ.data.id, acqCurrentQ.data);
    return Array.from(acqMap.values()).map((i) => ({ value: i.id, label: i.name }));
  }, [acqSearchQ.data, acqCurrentQ.data]);

  const [acqListOpen, setAcqListOpen] = useState(false);

  if (acqReadOnly) {
    if (!acqValue) return <Typography.Text type="secondary">—</Typography.Text>;
    if (acqCurrentQ.isLoading) return <Spin size="small" />;
    if (acqCurrentQ.data) {
      return <Tag style={{ fontSize: 13, padding: '2px 8px' }} title={`id: ${acqValue}`}>{acqCurrentQ.data.name}</Tag>;
    }
    return <Tag color="default" title={`id: ${acqValue}`}>[BAS] {String(acqValue).slice(0, 8)}…</Tag>;
  }

  const acqHandleChange = (acqNewId: string | undefined) => {
    if (!acqOnChange) return;
    if (!acqNewId) { acqOnChange(null); return; }
    const acqLabel = acqOptions.find((o) => o.value === acqNewId)?.label;
    acqOnChange(acqNewId, acqLabel);
  };

  return (
    <>
      <Space.Compact style={{ width: '100%' }}>
        <Select
          showSearch
          allowClear={acqAllowClear}
          filterOption={false}
          placeholder={`Оберіть ${acqTitle ?? acqEntityName}…`}
          size={acqSize}
          disabled={acqDisabled}
          value={acqValue ?? undefined}
          onChange={acqHandleChange}
          onSearch={setAcqQuery}
          onBlur={() => setAcqQuery('')}
          notFoundContent={acqSearchQ.isLoading ? <Spin size="small" /> : (acqDebounced ? 'Нічого не знайдено' : 'Почніть друкувати')}
          options={acqOptions}
          style={{ width: '100%' }}
        />
        <Button
          size={acqSize}
          icon={<FolderOpenOutlined />}
          disabled={acqDisabled}
          onClick={() => setAcqListOpen(true)}
          title={`Відкрити довідник: ${acqTitle ?? acqEntityName}`}
        />
      </Space.Compact>
      <BasRefListModal
        acqOpen={acqListOpen}
        acqEntityName={acqEntityName}
        acqTitle={acqTitle ?? acqEntityName}
        acqOnCancel={() => setAcqListOpen(false)}
        acqOnSelect={(acqId, acqName) => {
          setAcqListOpen(false);
          if (acqOnChange) acqOnChange(acqId, acqName);
        }}
      />
    </>
  );
}

function BasRefListModal({
  acqOpen, acqEntityName, acqTitle, acqOnCancel, acqOnSelect,
}: {
  acqOpen: boolean;
  acqEntityName: string;
  acqTitle: string;
  acqOnCancel: () => void;
  acqOnSelect: (acqId: string, acqName: string) => void;
}) {
  const [acqQuery, setAcqQuery] = useState('');
  const [acqDebounced, setAcqDebounced] = useState('');
  const acqDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const [acqPageSize, setAcqPageSize] = useState(20);

  useEffect(() => {
    if (!acqOpen) { setAcqQuery(''); setAcqDebounced(''); }
  }, [acqOpen]);

  useEffect(() => {
    if (acqDebounceRef.current) clearTimeout(acqDebounceRef.current);
    acqDebounceRef.current = setTimeout(() => setAcqDebounced(acqQuery), 250);
    return () => { if (acqDebounceRef.current) clearTimeout(acqDebounceRef.current); };
  }, [acqQuery]);

  const acqListQ = useQuery({
    queryKey: ['basref-ext', acqEntityName, 'list', acqDebounced, acqPageSize],
    queryFn: () => acqFetchList(acqEntityName, acqDebounced, acqPageSize),
    enabled: acqOpen,
    staleTime: 30_000,
  });

  const acqRows = acqListQ.data ?? [];

  return (
    <Modal
      open={acqOpen}
      title={`Оберіть: ${acqTitle}`}
      onCancel={acqOnCancel}
      footer={null}
      width={720}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Input.Search
          placeholder="Пошук…"
          allowClear
          value={acqQuery}
          onChange={(e) => setAcqQuery(e.target.value)}
          autoFocus
        />
        <Table<BasRefDto>
          rowKey="id"
          size="small"
          dataSource={acqRows}
          columns={[
            {
              title: 'Код',
              dataIndex: 'code',
              width: 140,
              render: (v: string | undefined) => v ?? <Typography.Text type="secondary">—</Typography.Text>,
            },
            { title: 'Назва', dataIndex: 'name' },
          ]}
          loading={acqListQ.isLoading || acqListQ.isFetching}
          pagination={{
            pageSize: acqPageSize,
            onShowSizeChange: (_, size) => setAcqPageSize(size),
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
          }}
          onRow={(acqRow) => ({
            onClick: () => acqOnSelect(acqRow.id, acqRow.name),
            style: { cursor: 'pointer' },
          })}
        />
      </Space>
    </Modal>
  );
}
